import { BehaviorSubject, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { dependencies } from '../../configurations/dependencies/dependencies';
import { Dependencies } from '../../configurations/dependencies/dependencies.interface';
import { Action } from './action.type';
import { AppState, initialState } from './app.state';
import { appEffects, EffectFunction } from './effects';
import { appReducers } from './reducers';

class Store {
  private state$: BehaviorSubject<AppState>;
  public action$ = new Subject<Action>();

  // Stryker disable all
  constructor(
    private initialState: AppState,
    private reducers: Record<
      string,
      (state: AppState, action: Action) => AppState
    >,
    private appEffects: Array<EffectFunction>,
    private dependencies: Dependencies,
  ) {
    this.state$ = new BehaviorSubject(this.initialState);

    merge(
      ...this.appEffects.map((effect: EffectFunction) =>
        effect(this.action$.asObservable(), this.state$, this.dependencies),
      ),
    ).subscribe((action: Action) => this.dispatch(action));

    this.action$
      .pipe(filter((action: Action) => !!action))
      .subscribe((action: Action) => this.applyReducer(action));
  }
  // Stryker restore all

  private applyReducer(action: Action): void {
    // Stryker disable all
    const reducer = this.reducers[action.type];
    if (reducer) {
      this.state$.next({
        ...this.state$.getValue(),
        ...reducer(this.state$.getValue(), action),
      });
    }
    // Stryker restore all
  }

  getState(): AppState {
    return this.state$.getValue();
  }

  dispatch(action: Action): void {
    // Stryker disable next-line
    this.action$.next(action);
  }

  select<K>(selector: (state: AppState) => K): Observable<K> {
    return this.state$.pipe(map(selector), distinctUntilChanged());
  }
}

export const store = new Store(
  initialState,
  appReducers,
  appEffects,
  dependencies,
);
